import { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  FormControlLabel,
  FormGroup,
  Switch,
  IconButton,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import TextInput from "../../../../../../components/textinput/textinput";
import { useFormik } from "formik";
import * as yup from "yup";
// import { websiteRegex } from "../../../../../../utils/Constant";
import Loading from "../../../../../../components/loading/loading";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import { updateClientDetailsRequest } from "../../../../../../utils/APICalls";
import { useDispatch } from "react-redux";
import { getClientDetailsRequest } from "../../../../../../redux/clients/slice";
import { websiteRegex } from "../../../../../../utils/Constant";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
};

interface FormValues {
  clientName?: string;
  isVip?: boolean;
  website?: string;
  notifyOnAppointmentUpdates?: boolean;
}

const ValidationSchema = yup.object({
  clientName: yup.string().required("This field can't be empty"),
  website: yup.string().matches(websiteRegex, "Invalid Website"),
});

export default function EditClientDetails({
  showDialog,
  closeDialog,
  data,
}: props) {
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();

  const formik = useFormik<FormValues>({
    initialValues: {
      clientName: data?.clientName || "",
      isVip: data?.isVip || false,
      website: data?.website || "",
      notifyOnAppointmentUpdates: data?.notifyOnAppointmentUpdates || false,
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      updateClientDetails(values);
    },
  });

  const updateClientDetails = async (values: any) => {
    let finalWebsite = null;
    if (values.website.length > 0) {
      finalWebsite = values.website.includes("http")
        ? values.website
        : `https://${values.website}`;
    }
    const payload = {
      id: data?.id,
      clientName: values.clientName,
      website: finalWebsite,
      isVip: values.isVip,
      notifyOnAppointmentUpdates: values.notifyOnAppointmentUpdates,
    };
    setShowLoading(true);
    const response = await updateClientDetailsRequest(payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getClientDetailsRequest({ id: data?.id }));
      hideDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const hideDialog = () => {
    formik.resetForm();
    closeDialog();
  };

  return (
    <DialogContainer
      title="Edit Client - Client Details"
      showDialog={showDialog}
      closeDialog={hideDialog}
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box className="user-name-layout">
          <Typography className="label-txt">Client Name</Typography>
          <TextInput
            className="field-input"
            id="clientName"
            name="clientName"
            label="Client Name"
            variant="outlined"
            placeholder="Client Name"
            value={formik.values.clientName}
            onChange={formik.handleChange}
            error={
              formik.touched.clientName && Boolean(formik.errors.clientName)
            }
            helperText={formik.touched.clientName && formik.errors.clientName}
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Box
            sx={{
              width: 192,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
              VIP Client
            </Typography>
            <Tooltip
              title="This setting is internal only and never visible to the client. If activated, all orders from this client will contain a flag so we know to give this order special attention."
              arrow
              placement="top"
            >
              <IconButton sx={{ p: 0, mr: 2 }}>
                <InfoOutlinedIcon color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  id="isVip"
                  name="isVip"
                  checked={formik.values.isVip}
                  onChange={formik.handleChange}
                />
              }
              label={formik.values.isVip ? "Yes" : "No"}
            />
          </FormGroup>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Company ID</Typography>
          <Typography>{data?.companyId}</Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Website</Typography>
          <TextInput
            className="field-input"
            id="website"
            name="website"
            label="Website"
            variant="outlined"
            placeholder="Website"
            value={formik.values.website}
            onChange={formik.handleChange}
            error={formik.touched.website && Boolean(formik.errors.website)}
            helperText={formik.touched.website && formik.errors.website}
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Box
            sx={{
              width: 192,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
              Notify the Order
              <br />
              Contact About
              <br />
              Appointment
              <br />
              Updates?
            </Typography>
            <Tooltip
              title="When toggled 'on', an email will be sent to the assigned order contact whenever an inspector schedules or reschedules an appointment for that order."
              arrow
              placement="top"
            >
              <IconButton sx={{ p: 0, mr: 2 }}>
                <InfoOutlinedIcon color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  id="notifyOnAppointmentUpdates"
                  name="notifyOnAppointmentUpdates"
                  checked={formik.values.notifyOnAppointmentUpdates}
                  onChange={formik.handleChange}
                />
              }
              label={formik.values.notifyOnAppointmentUpdates ? "Yes" : "No"}
            />
          </FormGroup>
        </Box>
      </form>
      {showLoading && <Loading />}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
    </DialogContainer>
  );
}
